/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import HotelStrategyStrategyDetailWithTabsBaseController from 'lumen/controllers/hotel/strategy/strategy-detail-with-tabs-base';
import {
  COLUMN_PROPERTIES,
  NO_DATA_SUBSORT,
  SUBTYPE_VALUES_KEY_STRATEGY,
  TYPE_ID_MEALTYPE,
} from 'lumen/utils/constants/rate-strategy-constants';
import { formatTableRate } from 'lumen/utils/strategy/detail-view-helpers';
import { timeout } from 'lumen/utils/waiters';

export default class HotelStrategyMealtypeController extends HotelStrategyStrategyDetailWithTabsBaseController {
  @service('hotel-and-market') hotelAndMarketService;
  @service intl;
  @service simpleExport;

  @tracked entity = SUBTYPE_VALUES_KEY_STRATEGY;

  typeId = TYPE_ID_MEALTYPE;

  exportRSTableTask = task({ drop: true }, async () => {
    const sheetName =
      this.typesDataPerId[this.typeId].formattedTypeName.toString();

    const headers = [
      this.intl.t('general.hotels').toString(),
      this.intl.t('rates.meal.room_only').toString(),
    ];

    this.subtypes.forEach((subtype) => {
      // Add a label between brackets since grouped columns are not possible in the simple exports
      const subtypeLabel = subtype.formattedLabel[SUBTYPE_VALUES_KEY_STRATEGY];
      headers.push(
        `${this.intl.t('strategy.mealtype.supplement')} (${subtypeLabel})`,
      );
      headers.push(`${this.intl.t('rates.rate')} (${subtypeLabel})`);
    });

    const content = [];
    this.mealtypeDataPerHotelInfo.forEach((rowObject) => {
      const row = [
        rowObject[COLUMN_PROPERTIES.HOTELINFO].formattedValue,
        formatTableRate(
          rowObject[COLUMN_PROPERTIES.ROOM_ONLY][COLUMN_PROPERTIES.RATE],
        ),
      ];

      this.subtypes.forEach((subtype) => {
        row.push(
          formatTableRate(rowObject[subtype.id][COLUMN_PROPERTIES.SUPPLEMENT]),
        );
        row.push(
          formatTableRate(rowObject[subtype.id][COLUMN_PROPERTIES.RATE]),
        );
      });

      content.push(row);
    });

    const postData = JSON.stringify({ headers, content, sheetName });
    this.simpleExport.execute(postData, this.hotelAndMarketService.hotel?.id);

    // Add a cooldown before the export can be triggered again
    // Prevents multiple downloads on double click or worse: Chrome disabling downloads from mylighthouse.com
    await timeout();
  });

  @computed(
    'hasRSData',
    'selectedCompetitors',
    'subtypes',
    'typeId',
    'typesDataPerId',
  )
  get mealtypeDataPerHotelInfo() {
    const mealtypeDataPerHotelInfo = new Map();

    if (this.hasRSData) {
      const roomOnlyStrategies = [
        ...this.typesDataPerId[this.typeId].rssubtypes,
      ].filter((subtype) => subtype.isMealtypeRoomOnlySubtype)[0]
        .filteredStrategies;
      const roomOnlyStrategyMapping = new Map();

      roomOnlyStrategies.forEach((strategy) => {
        roomOnlyStrategyMapping.set(strategy.hotelinfo.id, strategy);
      });

      // Initialize Map
      this.selectedCompetitors.forEach((hotelinfo) => {
        let roomOnlyPriceColumnProperties = {
          value: -1,
          formattedValue: this.intl.t('rates.meal.missing.5'),
          baserate: null,
          hasNoData: true,
          subsortOrder: NO_DATA_SUBSORT.MISSING,
        };

        if (roomOnlyStrategyMapping.has(hotelinfo.id)) {
          roomOnlyPriceColumnProperties = {
            value: roomOnlyStrategyMapping.get(hotelinfo.id).discountedPrice,
            formattedValue: roomOnlyStrategyMapping.get(hotelinfo.id)
              .formattedDiscountedPrice,
            baserate: roomOnlyStrategyMapping.get(hotelinfo.id).rsbaserate,
            hasNoData: roomOnlyStrategyMapping.get(hotelinfo.id).hasNoData,
            subsortOrder: roomOnlyStrategyMapping.get(hotelinfo.id)
              .subsortOrder,
          };
        }

        const tableColumns = {
          [COLUMN_PROPERTIES.HOTELINFO]: {
            id: hotelinfo.id,
            formattedValue: hotelinfo.name,
            isOwnHotelInfo:
              this.hotelAndMarketService.isOwnHotelInfo(hotelinfo),
          },
          [COLUMN_PROPERTIES.ROOM_ONLY]: {
            [COLUMN_PROPERTIES.RATE]: roomOnlyPriceColumnProperties,
          },
        };

        // Add a dummy object for each subtype (corresponds to a grouped column in the detail view)
        this.subtypes.forEach((subtype) => {
          tableColumns[subtype.id] = {};
        });

        mealtypeDataPerHotelInfo.set(hotelinfo.id, tableColumns);
      });

      this.subtypes.forEach((subtype) => {
        subtype.filteredStrategies.forEach((strategy) => {
          mealtypeDataPerHotelInfo.get(strategy.hotelinfo.id)[subtype.id][
            COLUMN_PROPERTIES.SUPPLEMENT
          ] = {
            value: strategy.supplementOrDiscount,
            formattedValue: strategy.formattedValue,
            hasNoData: strategy.hasNoData,
            subsortOrder: strategy.subsortOrder,
            isNonComputable: strategy.isNonComputable,
          };

          mealtypeDataPerHotelInfo.get(strategy.hotelinfo.id)[subtype.id][
            COLUMN_PROPERTIES.RATE
          ] = {
            value: strategy.discountedPrice,
            formattedValue: strategy.formattedDiscountedPrice,
            baserate: strategy.rsbaserate,
            // A mealtype strategy can be non-computable but still have a rate/discountedPrice, we don't want to handle it as "no data" in this case
            hasNoData: strategy.discountedPrice <= 0,
            subsortOrder: strategy.subsortOrder,
          };
        });
      });
    }
    return mealtypeDataPerHotelInfo;
  }
}
